interface Array<T> {
    /**
     * Returns a deep copy of a random element from the array.
     * If the array is empty, returns `undefined`.
     * 
     * The original array is not modified, and the returned element
     * is a deep-cloned copy (not a reference to the original).
     */
    randomDeepCopy(): T | undefined;
    /**
     * Returns one or more random elements from the array.
     * 
     * @param count - Number of elements to return. Defaults to 1.
     * If count > 1, returns an array of unique random elements.
     * If the array is empty, returns `undefined` or an empty array (depending on count).
     * 
     * @returns A single random element or an array of random elements.
     */
    random(count?: number): T | T[] | undefined;
    /**
     * Shuffles the array in place using the Fisher–Yates algorithm.
     * 
     * The original array is modified, and the same instance is returned
     * for chaining.
     *
     * @returns The shuffled array (same instance).
     */
    shuffle(): T[];
}

Array.prototype.randomDeepCopy = function <T>(this: T[]): T | undefined {
    if (this.length === 0) return undefined;

    const randomIndex = Math.floor(Math.random() * this.length);
    const item = this[randomIndex];

    if (typeof structuredClone === 'function') {
        return structuredClone(item);
    } else {
        return JSON.parse(JSON.stringify(item)); // Fallback deep copy
    }
};

Array.prototype.random = function <T>(this: T[], count: number = 1): T | T[] | undefined {
    if (this.length === 0) return count > 1 ? [] : undefined;

    if (count <= 1) {
        // Return a single random element
        return this[Math.floor(Math.random() * this.length)];
    }

    // Clamp count to array length
    const actualCount = Math.min(count, this.length);

    // Make a shallow copy to avoid modifying the original array
    const shuffled = [...this].shuffle();

    // Return the first N random elements
    return shuffled.slice(0, actualCount);
};

Array.prototype.shuffle = function <T>(this: T[]): T[] {
    for (let i = this.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [this[i], this[j]] = [this[j], this[i]];
    }
    return this;
};